import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './LyricConfirm.css';

/**
 * The standard Lyric confirmation dialog. Use this everywhere instead of the
 * native window.confirm/alert/prompt so all popups look consistent across the
 * app. Render it (controlled) with `isOpen` and handle `onConfirm`/`onCancel`.
 *
 * Props:
 *   isOpen        show/hide the dialog
 *   title         heading text
 *   message       body text (string or node)
 *   confirmLabel  primary button text (default "Confirm")
 *   cancelLabel   secondary button text (default "Cancel")
 *   tone          'default' | 'danger' (styles the confirm button)
 *   onConfirm     called when the user confirms (Enter or primary button)
 *   onCancel      called when the user cancels (Esc, overlay click, or Cancel)
 *   busy          when true, disables the buttons (action in flight)
 *
 *   requireText   GitHub-style guard: when set, the user must type this exact
 *                 string (e.g. the app name) before Confirm is enabled. Used for
 *                 destructive actions like deleting an app.
 *   requireLabel  label shown above the type-to-confirm input
 *   requirePlaceholder  placeholder for the input
 */
const LyricConfirm = ({
  isOpen,
  title = 'Are you sure?',
  message = '',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',
  onConfirm,
  onCancel,
  busy = false,
  requireText = null,
  requireLabel = null,
  requirePlaceholder = '',
}) => {
  const overlayRef = useRef(null);
  const inputRef = useRef(null);
  const [typed, setTyped] = useState('');

  // The type-to-confirm guard is satisfied only on an exact (trimmed) match.
  const needsMatch = typeof requireText === 'string' && requireText.length > 0;
  const matched = !needsMatch || typed.trim() === requireText;
  const canConfirm = matched && !busy;

  // Reset the typed value whenever the dialog (re)opens.
  useEffect(() => {
    if (isOpen) setTyped('');
  }, [isOpen, requireText]);

  // Focus the input when a type-to-confirm guard is active.
  useEffect(() => {
    if (isOpen && needsMatch && inputRef.current) {
      const id = setTimeout(() => { try { inputRef.current.focus(); } catch { /* ignore */ } }, 0);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [isOpen, needsMatch]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); if (onCancel) onCancel(); }
      else if (e.key === 'Enter') {
        e.preventDefault();
        if (canConfirm && onConfirm) onConfirm();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [isOpen, onConfirm, onCancel, canConfirm]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="lyric-confirm-overlay"
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current && onCancel) onCancel(); }}
    >
      <div className="lyric-confirm-card" role="dialog" aria-modal="true" aria-label={title}>
        <div className="lyric-confirm-header">
          <span className="lyric-confirm-brand" aria-hidden="true">🧭</span>
          <h3>{title}</h3>
        </div>
        {message ? <div className="lyric-confirm-body">{message}</div> : null}
        {needsMatch ? (
          <div className="lyric-confirm-require">
            {requireLabel ? (
              <label className="lyric-confirm-require-label" htmlFor="lyric-confirm-require-text">{requireLabel}</label>
            ) : (
              <label className="lyric-confirm-require-label" htmlFor="lyric-confirm-require-text">
                Type <code>{requireText}</code> to confirm
              </label>
            )}
            <input
              ref={inputRef}
              id="lyric-confirm-require-text"
              type="text"
              className="lyric-confirm-require-input"
              value={typed}
              placeholder={requirePlaceholder}
              autoComplete="off"
              spellCheck={false}
              onChange={(e) => setTyped(e.target.value)}
            />
          </div>
        ) : null}
        <div className="lyric-confirm-footer">
          <button type="button" className="lyric-confirm-btn lyric-confirm-btn-secondary" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`lyric-confirm-btn ${tone === 'danger' ? 'lyric-confirm-btn-danger' : 'lyric-confirm-btn-primary'}`}
            onClick={canConfirm ? onConfirm : undefined}
            disabled={!canConfirm}
            autoFocus={!needsMatch}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default LyricConfirm;
